import React from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ChartBarIcon,
  CpuChipIcon,
  GlobeAltIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ScaleIcon,
  SparklesIcon,
  ClockIcon,
  CheckCircleIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline';

const WhitepaperPage: React.FC = () => {
  const version = "1.0";
  const releaseDate = "January 2026";

  const sections = [
    { id: 'abstract', title: 'Abstract' },
    { id: 'introduction', title: '1. Introduction' },
    { id: 'problem', title: '2. Problem Statement' },
    { id: 'solution', title: '3. The TEMPLUM Solution' },
    { id: 'auction-mechanics', title: '4. Auction Mechanics' },
    { id: 'tokenomics', title: '5. NDH Token Economics' },
    { id: 'staking', title: '6. Staking & Tiers' },
    { id: 'governance', title: '7. DAO Governance' },
    { id: 'technical', title: '8. Technical Architecture' },
    { id: 'security', title: '9. Security' },
    { id: 'ecosystem', title: '10. NARDIHA Ecosystem' },
    { id: 'roadmap', title: '11. Roadmap' },
    { id: 'team', title: '12. Team' },
    { id: 'conclusion', title: '13. Conclusion' },
  ];

  const auctionTypes = [
    {
      name: 'English Auction',
      description: 'Traditional ascending price auction where bidders openly compete by placing increasingly higher bids.',
      icon: ArrowTrendingUpIcon,
      features: ['Open bidding', 'Ascending prices', 'Highest bidder wins', 'Transparent process']
    },
    {
      name: 'Dutch Auction',
      description: 'Descending price auction starting high and decreasing until a buyer accepts the current price.',
      icon: ChartBarIcon,
      features: ['Descending prices', 'Fast resolution', 'Price discovery', 'Single-bid wins']
    },
    {
      name: 'Vickrey Auction',
      description: 'Sealed-bid second-price auction where winner pays the second-highest bid amount.',
      icon: ShieldCheckIcon,
      features: ['Sealed bids', 'Second-price payment', 'Truthful bidding incentive', 'Privacy-preserving']
    },
    {
      name: 'Candle Auction',
      description: 'Auction with random ending time preventing last-second sniping and encouraging early honest bidding.',
      icon: ClockIcon,
      features: ['Random end time', 'Anti-sniping', 'Fair competition', 'Prevents manipulation']
    }
  ];

  const tierBenefits = [
    {
      tier: 'Bronze',
      stakeRequired: '1,000 NDH',
      color: 'from-amber-700 to-amber-900',
      benefits: ['Basic auction access', '5% fee discount', 'Standard support', 'Basic analytics']
    },
    {
      tier: 'Silver',
      stakeRequired: '10,000 NDH',
      color: 'from-gray-400 to-gray-600',
      benefits: ['Priority auction access', '15% fee discount', 'Priority support', 'Advanced analytics', 'Early notifications']
    },
    {
      tier: 'Gold',
      stakeRequired: '50,000 NDH',
      color: 'from-yellow-500 to-yellow-700',
      benefits: ['Exclusive auctions access', '25% fee discount', 'Dedicated support', 'Full analytics suite', 'Governance voting power', 'Curation rights']
    },
    {
      tier: 'Platinum',
      stakeRequired: '100,000 NDH',
      color: 'from-purple-400 to-purple-600',
      benefits: ['All Gold benefits', '35% fee discount', 'Private sales access', 'White-glove service', 'Enhanced governance weight', 'Revenue sharing']
    }
  ];

  const tokenDistribution = [
    { category: 'Public Sale', percentage: 25, color: 'bg-primary-500' },
    { category: 'Ecosystem & Rewards', percentage: 30, color: 'bg-green-500' },
    { category: 'Team & Advisors', percentage: 15, color: 'bg-blue-500' },
    { category: 'Treasury', percentage: 15, color: 'bg-purple-500' },
    { category: 'Liquidity', percentage: 10, color: 'bg-yellow-500' },
    { category: 'Strategic Partners', percentage: 5, color: 'bg-pink-500' },
  ];

  const roadmapItems = [
    {
      phase: 'Phase 1',
      title: 'Foundation',
      status: 'completed',
      quarter: 'Q1-Q2 2025',
      items: ['Smart contract development', 'Core platform architecture', 'Security audits', 'Testnet deployment']
    },
    {
      phase: 'Phase 2',
      title: 'Launch',
      status: 'completed',
      quarter: 'Q3-Q4 2025',
      items: ['Mainnet deployment (Polygon)', 'NDH token launch', 'Initial auction listings', 'Staking mechanism activation']
    },
    {
      phase: 'Phase 3',
      title: 'Growth',
      status: 'in-progress',
      quarter: 'Q1-Q2 2026',
      items: ['DAO governance activation', 'Cross-chain integration', 'Mobile app launch', 'Partner integrations']
    },
    {
      phase: 'Phase 4',
      title: 'Expansion',
      status: 'upcoming',
      quarter: 'Q3-Q4 2026',
      items: ['Multi-chain deployment', 'Enterprise solutions', 'Advanced AI curation', 'Global partnerships']
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <DocumentTextIcon className="w-16 h-16 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              TEMPLUM Whitepaper
            </h1>
            <p className="text-xl text-gray-400 mb-6">
              The Future of Decentralized Auctions
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 mb-8">
              <span className="px-3 py-1 bg-dark-800 rounded-full">Version {version}</span>
              <span className="px-3 py-1 bg-dark-800 rounded-full">{releaseDate}</span>
              <span className="px-3 py-1 bg-dark-800 rounded-full">NARDIHA Holdings</span>
            </div>
            <a
              href="/TEMPLUM_Whitepaper_v1.pdf"
              download
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
            >
              <DocumentTextIcon className="w-5 h-5" />
              Download PDF
            </a>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Table of Contents</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-gray-400 hover:text-primary-400 transition-colors py-2 px-3 bg-dark-800/50 rounded-lg hover:bg-dark-800"
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">

            {/* Abstract */}
            <section id="abstract" className="mb-16 scroll-mt-24">
              <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-2xl p-8 border border-primary-500/30">
                <h2 className="text-3xl font-bold text-white mb-6">Abstract</h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  TEMPLUM is a decentralized auction platform built on Polygon blockchain, designed to revolutionize how high-value assets are traded globally. By combining advanced auction mechanisms, DAO governance, and a sophisticated tier-based reward system, TEMPLUM creates a transparent, secure, and efficient marketplace for digital and tokenized real-world assets.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  This whitepaper outlines the technical architecture, economic model, governance framework, and strategic vision of the TEMPLUM platform as part of the broader NARDIHA Holdings ecosystem.
                </p>
              </div>
            </section>

            {/* Section 1: Introduction */}
            <section id="introduction" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">1</span>
                Introduction
              </h2>
              <div className="prose prose-invert prose-lg max-w-none">
                <p className="text-gray-300 mb-4">
                  The global auction market represents over $70 billion in annual transactions, yet remains dominated by centralized intermediaries charging excessive fees, lacking transparency, and limiting participation to select bidders. The advent of blockchain technology presents an unprecedented opportunity to democratize access to high-value auctions while ensuring security and trust.
                </p>
                <p className="text-gray-300 mb-4">
                  <strong className="text-white">TEMPLUM</strong> (Platform #20 in the NARDIHA Ecosystem) emerges as a comprehensive solution, offering:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                  <li>Multiple auction mechanisms optimized for different asset types</li>
                  <li>Complete transparency through on-chain verification</li>
                  <li>Reduced fees through smart contract automation</li>
                  <li>Global accessibility without geographic barriers</li>
                  <li>DAO governance ensuring community-driven development</li>
                </ul>
              </div>
            </section>

            {/* Section 2: Problem Statement */}
            <section id="problem" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">2</span>
                Problem Statement
              </h2>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <p className="text-gray-300 mb-6">
                  Traditional auction houses and online platforms suffer from several critical issues:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">High Fees</h4>
                    <p className="text-gray-400 text-sm">Traditional auction houses charge 15-25% buyer's premiums plus seller commissions</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">Lack of Transparency</h4>
                    <p className="text-gray-400 text-sm">Opaque bidding processes and potential for manipulation or shill bidding</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">Geographic Restrictions</h4>
                    <p className="text-gray-400 text-sm">Limited access for international participants due to regulations and logistics</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">Settlement Delays</h4>
                    <p className="text-gray-400 text-sm">Complex payment processing with settlement taking days or weeks</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">Counterparty Risk</h4>
                    <p className="text-gray-400 text-sm">Trust required in intermediaries with potential for fraud</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">Limited Innovation</h4>
                    <p className="text-gray-400 text-sm">Outdated auction mechanisms unable to adapt to digital assets</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3: The TEMPLUM Solution */}
            <section id="solution" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">3</span>
                The TEMPLUM Solution
              </h2>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 mb-6">
                <p className="text-gray-300 mb-6">
                  TEMPLUM addresses these challenges through a comprehensive blockchain-based platform:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <CurrencyDollarIcon className="w-10 h-10 text-green-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Low Fees</h4>
                    <p className="text-gray-400 text-sm">2.5% platform fee with discounts for stakers (up to 35% reduction)</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <CubeTransparentIcon className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Full Transparency</h4>
                    <p className="text-gray-400 text-sm">All bids and transactions recorded immutably on Polygon</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <GlobeAltIcon className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Global Access</h4>
                    <p className="text-gray-400 text-sm">24/7 participation from anywhere with crypto wallet</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <ClockIcon className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Instant Settlement</h4>
                    <p className="text-gray-400 text-sm">Smart contract escrow with immediate fund release</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <ShieldCheckIcon className="w-10 h-10 text-red-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Trustless Security</h4>
                    <p className="text-gray-400 text-sm">No intermediary risk - code is law</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <SparklesIcon className="w-10 h-10 text-pink-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Multiple Formats</h4>
                    <p className="text-gray-400 text-sm">4 auction types optimized for different scenarios</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4: Auction Mechanics */}
            <section id="auction-mechanics" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">4</span>
                Auction Mechanics
              </h2>
              <p className="text-gray-300 mb-6">
                TEMPLUM supports four distinct auction mechanisms, each optimized for specific use cases:
              </p>
              <div className="space-y-6">
                {auctionTypes.map((auction, idx) => {
                  const Icon = auction.icon;
                  return (
                    <div key={idx} className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-primary-500/20 rounded-xl">
                          <Icon className="w-8 h-8 text-primary-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-2">{auction.name}</h3>
                          <p className="text-gray-300 mb-4">{auction.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {auction.features.map((feature, fidx) => (
                              <span key={fidx} className="px-3 py-1 bg-dark-700 text-gray-400 text-sm rounded-full">
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Section 5: Tokenomics */}
            <section id="tokenomics" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">5</span>
                NDH Token Economics
              </h2>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 mb-6">
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Token Overview</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-dark-600">
                        <span className="text-gray-400">Token Name</span>
                        <span className="text-white font-semibold">NARDIHA Token</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-dark-600">
                        <span className="text-gray-400">Symbol</span>
                        <span className="text-white font-semibold">NDH</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-dark-600">
                        <span className="text-gray-400">Network</span>
                        <span className="text-white font-semibold">Polygon (MATIC)</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-dark-600">
                        <span className="text-gray-400">Total Supply</span>
                        <span className="text-white font-semibold">1,000,000,000 NDH</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-400">Token Type</span>
                        <span className="text-white font-semibold">ERC-20</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Token Utility</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3 text-gray-300">
                        <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                        Platform fee payments (discounted)
                      </li>
                      <li className="flex items-center gap-3 text-gray-300">
                        <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                        Staking for tier benefits
                      </li>
                      <li className="flex items-center gap-3 text-gray-300">
                        <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                        Governance voting rights
                      </li>
                      <li className="flex items-center gap-3 text-gray-300">
                        <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                        Auction participation requirements
                      </li>
                      <li className="flex items-center gap-3 text-gray-300">
                        <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                        Cross-platform ecosystem rewards
                      </li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-white mb-4">Token Distribution</h3>
                <div className="space-y-3">
                  {tokenDistribution.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">{item.category}</span>
                        <span className="text-white">{item.percentage}%</span>
                      </div>
                      <div className="h-3 bg-dark-600 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} rounded-full`}
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Section 6: Staking & Tiers */}
            <section id="staking" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">6</span>
                Staking & Tiers
              </h2>
              <p className="text-gray-300 mb-6">
                TEMPLUM implements a sophisticated tier system that rewards token holders with increasing benefits based on their staking commitment:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {tierBenefits.map((tier, idx) => (
                  <div key={idx} className={`bg-gradient-to-br ${tier.color} rounded-xl p-6`}>
                    <h3 className="text-2xl font-bold text-white mb-2">{tier.tier}</h3>
                    <p className="text-white/80 text-sm mb-4">Required: {tier.stakeRequired}</p>
                    <ul className="space-y-2">
                      {tier.benefits.map((benefit, bidx) => (
                        <li key={bidx} className="flex items-center gap-2 text-white/90 text-sm">
                          <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 7: DAO Governance */}
            <section id="governance" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">7</span>
                DAO Governance
              </h2>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <p className="text-gray-300 mb-6">
                  TEMPLUM operates as a Decentralized Autonomous Organization (DAO), empowering token holders to shape the platform's future:
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <ScaleIcon className="w-8 h-8 text-primary-400 mb-3" />
                    <h4 className="text-white font-semibold mb-2">Voting Power</h4>
                    <p className="text-gray-400 text-sm">
                      Voting power is proportional to staked NDH tokens. Higher tiers receive enhanced voting weight multipliers.
                    </p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <DocumentTextIcon className="w-8 h-8 text-blue-400 mb-3" />
                    <h4 className="text-white font-semibold mb-2">Proposal Types</h4>
                    <p className="text-gray-400 text-sm">
                      Platform upgrades, fee adjustments, new auction types, treasury allocation, and partnership approvals.
                    </p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <ClockIcon className="w-8 h-8 text-green-400 mb-3" />
                    <h4 className="text-white font-semibold mb-2">Voting Period</h4>
                    <p className="text-gray-400 text-sm">
                      7-day voting period with 48-hour timelock for implementation. Emergency proposals require 66% supermajority.
                    </p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <UserGroupIcon className="w-8 h-8 text-purple-400 mb-3" />
                    <h4 className="text-white font-semibold mb-2">Quorum</h4>
                    <p className="text-gray-400 text-sm">
                      Minimum 10% of circulating supply participation required. Proposals pass with &gt;50% approval.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 8: Technical Architecture */}
            <section id="technical" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">8</span>
                Technical Architecture
              </h2>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Smart Contracts</h3>
                    <div className="space-y-3 font-mono text-sm">
                      <div className="p-3 bg-dark-900/50 rounded-lg">
                        <span className="text-primary-400">AuctionFactory.sol</span>
                        <p className="text-gray-400 text-xs mt-1">Creates and manages auction instances</p>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg">
                        <span className="text-primary-400">EnglishAuction.sol</span>
                        <p className="text-gray-400 text-xs mt-1">Ascending price auction logic</p>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg">
                        <span className="text-primary-400">DutchAuction.sol</span>
                        <p className="text-gray-400 text-xs mt-1">Descending price auction logic</p>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg">
                        <span className="text-primary-400">VickreyAuction.sol</span>
                        <p className="text-gray-400 text-xs mt-1">Sealed-bid second-price logic</p>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg">
                        <span className="text-primary-400">CandleAuction.sol</span>
                        <p className="text-gray-400 text-xs mt-1">Random-end anti-sniping logic</p>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg">
                        <span className="text-primary-400">StakingPool.sol</span>
                        <p className="text-gray-400 text-xs mt-1">NDH staking and tier management</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Technology Stack</h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-dark-900/50 rounded-lg flex items-center gap-3">
                        <CpuChipIcon className="w-6 h-6 text-purple-400" />
                        <div>
                          <span className="text-white font-medium">Polygon (Matic)</span>
                          <p className="text-gray-400 text-xs">Chain ID: 137</p>
                        </div>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg flex items-center gap-3">
                        <CubeTransparentIcon className="w-6 h-6 text-blue-400" />
                        <div>
                          <span className="text-white font-medium">Solidity 0.8.x</span>
                          <p className="text-gray-400 text-xs">Smart contract language</p>
                        </div>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg flex items-center gap-3">
                        <GlobeAltIcon className="w-6 h-6 text-green-400" />
                        <div>
                          <span className="text-white font-medium">IPFS</span>
                          <p className="text-gray-400 text-xs">Decentralized asset metadata storage</p>
                        </div>
                      </div>
                      <div className="p-3 bg-dark-900/50 rounded-lg flex items-center gap-3">
                        <ShieldCheckIcon className="w-6 h-6 text-red-400" />
                        <div>
                          <span className="text-white font-medium">Chainlink</span>
                          <p className="text-gray-400 text-xs">VRF for randomness, Price feeds</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 9: Security */}
            <section id="security" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">9</span>
                Security
              </h2>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <p className="text-gray-300 mb-6">
                  Security is paramount at TEMPLUM. Our multi-layered approach includes:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Smart Contract Audits</h4>
                    <p className="text-gray-400 text-sm">Comprehensive audits by leading firms including CertiK and Trail of Bits</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Bug Bounty Program</h4>
                    <p className="text-gray-400 text-sm">Up to $100,000 rewards for critical vulnerability discoveries</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Formal Verification</h4>
                    <p className="text-gray-400 text-sm">Mathematical proof of contract correctness for core functions</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Multi-sig Treasury</h4>
                    <p className="text-gray-400 text-sm">4-of-7 multi-signature wallet for treasury operations</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Timelock Controls</h4>
                    <p className="text-gray-400 text-sm">48-hour delay on critical contract upgrades</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Emergency Pause</h4>
                    <p className="text-gray-400 text-sm">Circuit breaker mechanism for immediate threat response</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 10: NARDIHA Ecosystem */}
            <section id="ecosystem" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">10</span>
                NARDIHA Ecosystem
              </h2>
              <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-xl p-6 border border-primary-500/30">
                <p className="text-gray-300 mb-6">
                  TEMPLUM is Platform #20 within the NARDIHA Holdings ecosystem, a comprehensive network of 50+ interconnected platforms sharing the NDH token economy:
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <BuildingLibraryIcon className="w-8 h-8 text-primary-400 mx-auto mb-2" />
                    <h4 className="text-white font-semibold">DeFi</h4>
                    <p className="text-gray-400 text-sm">DEX, lending, yield farming</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <SparklesIcon className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <h4 className="text-white font-semibold">NFTs</h4>
                    <p className="text-gray-400 text-sm">Marketplace, gaming, metaverse</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg text-center">
                    <BanknotesIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <h4 className="text-white font-semibold">RWA</h4>
                    <p className="text-gray-400 text-sm">Real-world asset tokenization</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm">
                  Cross-platform synergies enable users to leverage their NDH holdings across all NARDIHA platforms, maximizing utility and rewards.
                </p>
              </div>
            </section>

            {/* Section 11: Roadmap */}
            <section id="roadmap" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">11</span>
                Roadmap
              </h2>
              <div className="space-y-6">
                {roadmapItems.map((phase, idx) => (
                  <div key={idx} className={`bg-dark-800/50 rounded-xl p-6 border ${
                    phase.status === 'completed' ? 'border-green-500/30' :
                    phase.status === 'in-progress' ? 'border-primary-500/30' :
                    'border-dark-700'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className={`text-sm font-medium ${
                          phase.status === 'completed' ? 'text-green-400' :
                          phase.status === 'in-progress' ? 'text-primary-400' :
                          'text-gray-500'
                        }`}>{phase.phase}</span>
                        <h3 className="text-xl font-bold text-white">{phase.title}</h3>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-400 text-sm">{phase.quarter}</span>
                        <span className={`block text-xs px-2 py-1 rounded mt-1 ${
                          phase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          phase.status === 'in-progress' ? 'bg-primary-500/20 text-primary-400' :
                          'bg-dark-700 text-gray-500'
                        }`}>
                          {phase.status === 'completed' ? 'Completed' :
                           phase.status === 'in-progress' ? 'In Progress' : 'Upcoming'}
                        </span>
                      </div>
                    </div>
                    <ul className="grid md:grid-cols-2 gap-2">
                      {phase.items.map((item, iidx) => (
                        <li key={iidx} className="flex items-center gap-2 text-gray-400 text-sm">
                          <CheckCircleIcon className={`w-4 h-4 flex-shrink-0 ${
                            phase.status === 'completed' ? 'text-green-400' :
                            phase.status === 'in-progress' ? 'text-primary-400' :
                            'text-gray-600'
                          }`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 12: Team */}
            <section id="team" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">12</span>
                Team
              </h2>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <p className="text-gray-300 mb-6">
                  TEMPLUM is developed and operated by NARDIHA Holdings S.L., a Spanish technology company with deep expertise in blockchain, fintech, and enterprise software.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Core Development</h4>
                    <p className="text-gray-400 text-sm">Experienced blockchain engineers with backgrounds at leading crypto projects</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Legal & Compliance</h4>
                    <p className="text-gray-400 text-sm">Dedicated team ensuring regulatory compliance across jurisdictions</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Product & Design</h4>
                    <p className="text-gray-400 text-sm">User-centric design team focused on accessibility and experience</p>
                  </div>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <h4 className="text-white font-semibold mb-2">Business Development</h4>
                    <p className="text-gray-400 text-sm">Strategic partnerships with auction houses, galleries, and collectors</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 13: Conclusion */}
            <section id="conclusion" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center text-primary-400">13</span>
                Conclusion
              </h2>
              <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-xl p-8 border border-primary-500/30">
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  TEMPLUM represents a paradigm shift in how high-value assets are auctioned globally. By leveraging blockchain technology, we eliminate intermediaries, reduce fees, and create a truly transparent marketplace accessible to everyone.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  Our commitment to security, governance decentralization, and user experience positions TEMPLUM as the premier destination for both traditional collectors entering the Web3 space and crypto-native users seeking trusted auction infrastructure.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  As part of the NARDIHA ecosystem, TEMPLUM benefits from shared liquidity, cross-platform rewards, and a unified vision for the future of decentralized finance and digital ownership.
                </p>
              </div>
            </section>

            {/* Disclaimer */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mt-12">
              <h3 className="text-yellow-400 font-semibold mb-3">Disclaimer</h3>
              <p className="text-yellow-200/80 text-sm">
                This whitepaper is for informational purposes only and does not constitute financial, legal, or investment advice. The NDH token may be considered a security in certain jurisdictions. Participation in TEMPLUM auctions and token purchases involves substantial risk. Please consult with qualified professionals before making any decisions. Past performance does not guarantee future results.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default WhitepaperPage;
